// src/components/TypeGrid.jsx
import React, { useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { useSearch } from "../../context/SearchContext";
import Icon from "@mdi/react";
import {
  mdiCarHatchback,
  mdiCarSide,
  mdiCarSports,
  mdiSeatPassenger,
} from "@mdi/js";

// Seats exclude DRIVER
const TYPES = [
  { key: "Hatchback", title: "Hatchback", seatsText: "Seats: up to 4", icon: mdiCarHatchback },
  { key: "Sedan",     title: "Sedan",     seatsText: "Seats: up to 4", icon: mdiCarSide },
  { key: "SUV",       title: "SUV",       seatsText: "Seats: 4–7 (incl. MUV/MPV)", icon: mdiCarSports },
];

// Category-aware examples (single-line, truncates visually)
const EXAMPLES = {
  Hatchback: {
    normal: ["Swift", "i20", "Altroz", "Baleno"],
    premium: ["Mini Cooper", "Mini Cooper S", "VW Polo GTI", "Abarth 595"],
  },
  Sedan: {
    normal: ["City", "Verna", "Ciaz", "Slavia"],
    premium: ["Mercedes E-Class", "BMW 5 Series", "Audi A6", "Lexus ES"],
  },
  SUV: {
    normal: ["Creta", "Scorpio", "Innova", "XUV700"],
    premium: ["Range Rover", "Mercedes GLS", "BMW X7", "Audi Q7"],
  },
};

export default function TypeGrid() {
  const { types, toggleType, categories } = useSearch();

  const onKeyToggle = useCallback((e, key) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleType(key);
    }
  }, [toggleType]);

  const showNormal = useMemo(
    () => categories.includes("normal") || categories.length === 0,
    [categories],
  );
  const showLuxury = useMemo(
    () => categories.includes("premium"),
    [categories],
  );

  return (
    <>
      <div className="mb-2 hidden text-xs text-slate-500 md:block">Seats shown exclude the driver.</div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-6">
        {TYPES.map((t) => {
          const selected = types.includes(t.key);
          const normList = EXAMPLES[t.key]?.normal || [];
          const luxList  = EXAMPLES[t.key]?.premium || [];

          const titleFull = [
            showNormal && normList.length ? `Normal: ${normList.join(", ")}` : null,
            showLuxury && luxList.length ? `Luxury: ${luxList.join(", ")}` : null,
          ].filter(Boolean).join("  •  ");

          const muted = selected ? "text-white/60" : "text-slate-500";
          const badgeBase = "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold";
          const normalBadge = selected ? `${badgeBase} bg-white/10 text-white/80` : `${badgeBase} bg-slate-100 text-slate-700`;
          const luxuryBadge = selected ? `${badgeBase} bg-white/10 text-white/80` : `${badgeBase} bg-amber-50 text-amber-800`;

          return (
            <motion.button
              key={t.key}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => toggleType(t.key)}
              onKeyDown={(e) => onKeyToggle(e, t.key)}
              role="checkbox"
              aria-checked={selected}
              aria-label={`${t.title}${selected ? " selected" : ""}`}
              className={[
                "relative w-full text-left rounded-2xl border transition outline-none",
                "min-h-[104px] px-4 py-4 md:min-h-[122px] md:px-6 md:py-6",
                selected
                  ? "bg-black text-white border-black shadow-sm"
                  : "bg-white text-slate-900 border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md focus-visible:ring-4 focus-visible:ring-slate-900/10",
              ].join(" ")}
            >
              <div aria-hidden="true" className="absolute inset-x-0 -top-px h-[3px] rounded-t-2xl"
                   style={{ background: "var(--brand-gradient)" }} />

              {selected && (
                <span aria-hidden="true"
                      className="absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-[11px] font-bold text-black shadow md:right-4 md:top-4 md:h-6 md:w-6 md:text-xs">
                  ✓
                </span>
              )}

              <div className="flex items-center gap-3 md:gap-4">
                <span aria-hidden="true"
                      className={["grid h-10 w-10 place-items-center rounded-xl border md:h-12 md:w-12",
                        selected ? "border-white/20 bg-white/10" : "border-slate-200 bg-slate-50",
                      ].join(" ")}>
                  <Icon path={t.icon} size={0.9} className={selected ? "text-white" : "text-slate-600"} />
                </span>

                <div className="flex-1 min-w-0">
                  <div className="truncate text-[1.1rem] font-semibold leading-tight md:text-xl">{t.title}</div>

                  <div className={["mt-1 flex items-center gap-1.5 text-[13px] md:gap-2 md:text-sm", selected ? "text-white/80" : "text-slate-500"].join(" ")}>
                    <Icon path={mdiSeatPassenger} size={0.8} className={selected ? "text-white/80" : "text-slate-500"} />
                    <span className="truncate">{t.seatsText}</span>
                  </div>

                  <div className={`mt-1 space-y-1 text-[11px] md:text-xs ${muted}`} title={titleFull}>
                    {showNormal && normList.length > 0 && (
                      <div className="flex items-center gap-2 overflow-hidden">
                        <span className={`${normalBadge} shrink-0`}>Normal</span>
                        <span className="min-w-0 truncate">{normList.join(" · ")}</span>
                      </div>
                    )}
                    {showLuxury && luxList.length > 0 && (
                      <div className="flex items-center gap-2 overflow-hidden">
                        <span className={`${luxuryBadge} shrink-0`}>Luxury</span>
                        <span className="min-w-0 truncate">{luxList.join(" · ")}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </>
  );
}
